import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Power } from 'lucide-react';
import { useTranslation } from '../../../i18n';
import { Button } from '../../../components/Shared/Button';
import { Card } from '../../../components/Shared/Card';
import { Badge } from '../../../components/Shared/Badge';
import { EmptyState } from '../../../components/Shared/EmptyState';
import { ConfirmDialog } from '../../../components/Shared/ConfirmDialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/Shared/Table';
import { useAdminTenantStore } from '../../../store/useAdminTenantStore';
import { useSubscriptionPlanStore } from '../../../store/useSubscriptionPlanStore';
import { useTenantInvoiceStore } from '../../../store/useTenantInvoiceStore';
import {
  BusinessPlan,
  TenantInvoiceStatus,
  type TenantInvoiceDto,
} from '../../../api/types/admin';
import { formatDate } from '../../../utils/dateUtils';
import { formatMoney } from '../../../utils/money';

function statusVariant(s: number | undefined): 'default' | 'info' | 'success' | 'danger' {
  switch (s) {
    case TenantInvoiceStatus.Issued:
      return 'info';
    case TenantInvoiceStatus.Paid:
      return 'success';
    case TenantInvoiceStatus.Cancelled:
      return 'danger';
    case TenantInvoiceStatus.Draft:
    default:
      return 'default';
  }
}

export default function TenantAdminDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation(['admin', 'common']);

  const {
    tenants,
    statistics,
    saving,
    fetchAll,
    getById,
    fetchStatistics,
    activate,
    deactivate,
    changePlan,
  } = useAdminTenantStore();
  const { plans, fetchAll: fetchPlans } = useSubscriptionPlanStore();
  const { fetchForTenant } = useTenantInvoiceStore();

  const [loaded, setLoaded] = useState(false);
  const [tenantInvoices, setTenantInvoices] = useState<TenantInvoiceDto[]>([]);
  const [newPlan, setNewPlan] = useState<BusinessPlan | ''>('');
  const [proration, setProration] = useState(true);
  const [confirmChange, setConfirmChange] = useState(false);
  const [confirmToggle, setConfirmToggle] = useState<'activate' | 'deactivate' | null>(null);

  const tenant = useMemo(() => tenants.find((tn) => tn.id === id), [tenants, id]);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    (async () => {
      if (tenants.length === 0) await fetchAll();
      await getById(id);
      await fetchStatistics(id);
      if (plans.length === 0) await fetchPlans();
      const list = await fetchForTenant(id);
      if (!cancelled) {
        setTenantInvoices(list);
        setLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, fetchAll, fetchStatistics, fetchPlans, fetchForTenant, getById, plans.length, tenants.length]);

  if (!loaded) {
    return <p className="text-[13px] text-slate-500 text-center py-12">{t('common:actions.loading')}</p>;
  }

  if (!tenant) {
    return (
      <Card>
        <EmptyState
          variant="error"
          title={t('admin:tenants.errors.notFound')}
          action={
            <Link to="/admin/tenants">
              <Button variant="secondary" leftIcon={<ArrowLeft className="w-3.5 h-3.5" />}>
                {t('admin:tenants.actions.back')}
              </Button>
            </Link>
          }
        />
      </Card>
    );
  }

  const stats = statistics[tenant.id || ''];
  const currentPlanDto = plans.find((p) => p.businessPlan === tenant.businessPlan);
  const newPlanDto = newPlan !== '' ? plans.find((p) => p.businessPlan === newPlan) : null;
  const maxEmployees = currentPlanDto?.maxEmployees;

  const handleConfirmChange = async () => {
    if (newPlan === '') return;
    if (!tenant.id) return;
    const updated = await changePlan(tenant.id, newPlan as BusinessPlan, proration);
    setConfirmChange(false);
    if (updated) {
      setNewPlan('');
      const list = await fetchForTenant(tenant.id);
      setTenantInvoices(list);
    }
  };

  const handleConfirmToggle = async () => {
    if (!tenant.id) return;
    if (confirmToggle === 'activate') await activate(tenant.id);
    else if (confirmToggle === 'deactivate') await deactivate(tenant.id);
    setConfirmToggle(null);
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <Button
        variant="secondary"
        onClick={() => navigate('/admin/tenants')}
        leftIcon={<ArrowLeft className="w-3.5 h-3.5" strokeWidth={2} />}
      >
        {t('admin:tenants.actions.back')}
      </Button>

      <div className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-[22px] sm:text-[24px] font-semibold tracking-tight text-slate-900 truncate">
                {tenant.name}
              </h1>
              <Badge variant={tenant.isActive ? 'success' : 'default'}>
                {tenant.isActive
                  ? t('admin:tenants.filters.active')
                  : t('admin:tenants.filters.inactive')}
              </Badge>
            </div>
            <p className="text-[13px] text-slate-500 mt-0.5 font-mono">
              {tenant.domain || '—'}
            </p>
          </div>
        </div>
      </div>

      {/* Master data + Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card title={t('admin:tenants.sections.master')}>
          <dl className="space-y-2 text-[13px]">
            <Row label={t('admin:tenants.fields.name')} value={tenant.name} />
            <Row label={t('admin:tenants.fields.domain')} value={tenant.domain} />
            <Row
              label={t('admin:tenants.fields.plan')}
              value={t(`admin:tenants.planNames.${tenant.businessPlan ?? 0}`)}
            />
            <Row label={t('admin:tenants.fields.createdAt')} value={formatDate(tenant.createdAt)} />
            <Row
              label={t('admin:tenants.fields.contactEmail')}
              value={tenant.contactPerson?.email || '—'}
            />
            <Row
              label={t('admin:tenants.fields.lastUpdate')}
              value={tenant.updatedAt ? formatDate(tenant.updatedAt) : '—'}
            />
          </dl>
        </Card>

        <Card title={t('admin:tenants.stats.title')}>
          {stats ? (
            <dl className="space-y-2 text-[13px]">
              <Row
                label={t('admin:tenants.stats.employees')}
                value={
                  maxEmployees == null
                    ? `${stats.employeeCount ?? 0} / ${t('admin:tenants.stats.unlimited')}`
                    : `${stats.employeeCount ?? 0} / ${maxEmployees}`
                }
              />
              <Row label={t('admin:tenants.stats.customers')} value={stats.customerCount ?? 0} />
              <Row label={t('admin:tenants.stats.objects')} value={stats.objectCount ?? 0} />
              <Row
                label={t('admin:tenants.stats.activeOffers')}
                value={stats.activeOffersCount ?? 0}
              />
              <Row
                label={t('admin:tenants.stats.issuedInvoices')}
                value={stats.issuedInvoicesCount ?? 0}
              />
              <Row
                label={t('admin:tenants.stats.openTenantInvoices')}
                value={stats.openTenantInvoicesCount ?? 0}
              />
            </dl>
          ) : (
            <p className="text-[13px] text-slate-500">{t('common:actions.loading')}</p>
          )}
        </Card>
      </div>

      {/* Plan change */}
      <Card title={t('admin:tenants.sections.planChange')}>
        <div className="space-y-3">
          <p className="text-[13px] text-slate-600">
            {t('admin:tenants.changePlan.current', {
              plan: t(`admin:tenants.planNames.${tenant.businessPlan ?? 0}`),
            })}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3 items-end">
            <div>
              <label className="block text-[12px] font-medium text-slate-600 mb-1">
                {t('admin:tenants.changePlan.newPlan')}
              </label>
              <select
                value={String(newPlan)}
                onChange={(e) =>
                  setNewPlan(e.target.value === '' ? '' : (Number(e.target.value) as BusinessPlan))
                }
                className="w-full px-3 py-1.5 rounded-md bg-white border border-slate-200 text-[13px] text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#00A871]/30"
              >
                <option value="">—</option>
                {plans
                  .filter((p) => p.isActive && p.businessPlan !== tenant.businessPlan)
                  .map((p) => (
                    <option key={p.id} value={p.businessPlan}>
                      {p.name} ({t(`admin:tenants.planNames.${p.businessPlan ?? 0}`)})
                    </option>
                  ))}
              </select>
            </div>
            <Button
              onClick={() => setConfirmChange(true)}
              disabled={newPlan === '' || saving}
            >
              {t('admin:tenants.changePlan.submit')}
            </Button>
          </div>
          <label className="flex items-center gap-2 text-[12px] text-slate-600">
            <input
              type="checkbox"
              checked={proration}
              onChange={(e) => setProration(e.target.checked)}
              className="rounded border-slate-300"
            />
            {t('admin:tenants.changePlan.applyProration')}
          </label>
        </div>
      </Card>

      {/* Invoices */}
      <Card
        title={t('admin:tenants.sections.invoices')}
        actions={
          <Link to={`/admin/tenant-invoices?tenantId=${tenant.id}`}>
            <Button variant="secondary" size="sm">
              {t('admin:tenants.viewAllInvoices')}
            </Button>
          </Link>
        }
        noPadding
      >
        {tenantInvoices.length === 0 ? (
          <div className="p-4">
            <p className="text-[13px] text-slate-500">{t('admin:tenants.noInvoices')}</p>
          </div>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableHeader>{t('admin:tenantInvoices.fields.invoiceNumber')}</TableHeader>
                <TableHeader>{t('admin:tenantInvoices.fields.period')}</TableHeader>
                <TableHeader>{t('admin:tenantInvoices.fields.amount')}</TableHeader>
                <TableHeader>{t('admin:tenantInvoices.fields.status')}</TableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {tenantInvoices.slice(0, 5).map((inv) => (
                <TableRow
                  key={inv.id}
                  onClick={() => navigate(`/admin/tenant-invoices/${inv.id}`)}
                >
                  <TableCell className="font-mono text-[12px]">
                    {inv.invoiceNumber || t('admin:tenantInvoices.fields.draft')}
                  </TableCell>
                  <TableCell>
                    {formatDate(inv.periodStart)} – {formatDate(inv.periodEnd)}
                  </TableCell>
                  <TableCell>
                    {formatMoney(inv.grossAmount, { currency: inv.currency })}
                  </TableCell>
                  <TableCell>
                    <Badge variant={statusVariant(inv.status)}>
                      {t(`admin:tenantInvoices.status.${statusLabel(inv.status)}`)}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      {/* Danger zone */}
      <Card title={t('admin:tenants.sections.dangerZone')}>
        {tenant.isActive ? (
          <Button
            variant="danger"
            onClick={() => setConfirmToggle('deactivate')}
            disabled={saving}
            leftIcon={<Power className="w-3.5 h-3.5" strokeWidth={2} />}
          >
            {t('admin:tenants.actions.deactivate')}
          </Button>
        ) : (
          <Button
            onClick={() => setConfirmToggle('activate')}
            disabled={saving}
            leftIcon={<Power className="w-3.5 h-3.5" strokeWidth={2} />}
          >
            {t('admin:tenants.actions.activate')}
          </Button>
        )}
      </Card>

      <ConfirmDialog
        isOpen={confirmChange}
        onClose={() => setConfirmChange(false)}
        onConfirm={handleConfirmChange}
        title={t('admin:tenants.changePlan.confirmTitle')}
        message={t('admin:tenants.changePlan.confirmMessage', {
          name: tenant.name || '',
          plan: newPlanDto?.name || t(`admin:tenants.planNames.${newPlan === '' ? 0 : newPlan}`),
        })}
        variant="info"
        loading={saving}
      />

      <ConfirmDialog
        isOpen={confirmToggle === 'activate'}
        onClose={() => setConfirmToggle(null)}
        onConfirm={handleConfirmToggle}
        title={t('admin:tenants.confirm.activate.title')}
        message={t('admin:tenants.confirm.activate.message', { name: tenant.name || '' })}
        variant="info"
        loading={saving}
      />

      <ConfirmDialog
        isOpen={confirmToggle === 'deactivate'}
        onClose={() => setConfirmToggle(null)}
        onConfirm={handleConfirmToggle}
        title={t('admin:tenants.confirm.deactivate.title')}
        message={t('admin:tenants.confirm.deactivate.message', { name: tenant.name || '' })}
        loading={saving}
      />
    </div>
  );
}

function statusLabel(s: number | undefined): string {
  switch (s) {
    case TenantInvoiceStatus.Issued:
      return 'issued';
    case TenantInvoiceStatus.Paid:
      return 'paid';
    case TenantInvoiceStatus.Cancelled:
      return 'cancelled';
    case TenantInvoiceStatus.Draft:
    default:
      return 'draft';
  }
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline gap-2">
      <dt className="text-[12px] text-slate-500 min-w-[140px]">{label}</dt>
      <dd className="text-[13px] text-slate-900 font-medium tabular-nums">{value || '—'}</dd>
    </div>
  );
}
